/**
 * OneOffAppointmentForm
 * Feature: 005-clinic-scheduling (T011)
 *
 * Form for creating one-off (avulso) appointments.
 */

import { useEffect, useId } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useOverlapCheck } from '../hooks/use-overlap-check'
import { useOverlapPolicy } from '../hooks/use-overlap-policy'
import { useAppointmentLocationSuggestions } from '../hooks/use-appointment-location-suggestions'
import { ProfessionalMultiSelect } from './ProfessionalMultiSelect'
import {
  createOneOffAppointmentSchema,
  type CreateOneOffAppointmentFormData,
  type OverlapCheckResult,
} from '../types/scheduling.types'

interface ProfessionalOption {
  id: string
  full_name: string
}

const SESSION_TYPE_OPTIONS = [
  { value: 'Atendimento', label: 'Atendimento' },
  { value: 'Avaliação', label: 'Avaliação' },
  { value: 'Devolutiva', label: 'Devolutiva' },
  { value: 'Supervisão', label: 'Supervisão' },
  { value: 'Outro', label: 'Outro' },
]

interface OneOffAppointmentFormProps {
  patientId: string
  professionals: ProfessionalOption[]
  onSubmit: (data: CreateOneOffAppointmentFormData) => Promise<void>
  onOverlapDetected?: (result: OverlapCheckResult) => void
  isSubmitting?: boolean
  /** Optional pre-filled values, e.g. from a calendar slot click */
  initialValues?: {
    session_date?: string
    start_time?: string
    end_time?: string
  }
}

export function OneOffAppointmentForm({
  patientId,
  professionals,
  onSubmit,
  onOverlapDetected,
  isSubmitting = false,
  initialValues,
}: OneOffAppointmentFormProps) {
  const { checkOverlap, isChecking } = useOverlapCheck()
  const { data: policy } = useOverlapPolicy()
  const locationSuggestions = useAppointmentLocationSuggestions()
  const locationListId = useId().replaceAll(':', '')

  const form = useForm<CreateOneOffAppointmentFormData>({
    resolver: zodResolver(createOneOffAppointmentSchema),
    defaultValues: {
      patient_id: patientId,
      session_date: initialValues?.session_date ?? '',
      start_time: initialValues?.start_time ?? '',
      end_time: initialValues?.end_time ?? '',
      session_type: 'Atendimento',
      location_text: '',
      professional_ids: [],
      notes: '',
    },
  })

  const sessionDate = form.watch('session_date')
  const startTime = form.watch('start_time')
  const endTime = form.watch('end_time')
  // Serialize the array to a stable string so the effect only re-runs when IDs actually change
  const professionalIdsKey = (form.watch('professional_ids') ?? []).join(',')

  // Real-time overlap check
  useEffect(() => {
    const professionalIds = professionalIdsKey.split(',').filter(Boolean)
    if (
      !sessionDate ||
      !startTime ||
      !endTime ||
      !professionalIds.length ||
      startTime >= endTime
    )
      return
    if (!policy || policy === 'allowed') return

    const timer = setTimeout(async () => {
      const result = await checkOverlap(
        {
          professional_ids: professionalIds,
          session_date: sessionDate,
          start_time: startTime,
          end_time: endTime,
        },
        policy
      )
      if (result.has_conflicts) {
        onOverlapDetected?.(result)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [
    sessionDate,
    startTime,
    endTime,
    professionalIdsKey,
    policy,
    checkOverlap,
    onOverlapDetected,
  ])

  const handleSubmit = async (data: CreateOneOffAppointmentFormData) => {
    await onSubmit(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="session_date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Data do atendimento</FormLabel>
              <FormControl>
                <Input type="date" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="start_time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Horário de início</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="end_time"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Horário de término</FormLabel>
                <FormControl>
                  <Input type="time" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="session_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de atendimento</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SESSION_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="professional_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Profissionais</FormLabel>
              <FormControl>
                <ProfessionalMultiSelect
                  professionals={professionals}
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location_text"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Local (opcional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="Ex.: Sala 2, online, escola..."
                  list={locationListId}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                />
              </FormControl>
              <datalist id={locationListId}>
                {locationSuggestions.map((location) => (
                  <option key={location} value={location} />
                ))}
              </datalist>
              <FormDescription>
                Campo livre para sala, endereço, escola ou link de atendimento.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Observações (opcional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Observações sobre o atendimento..."
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isChecking && (
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" />
            Verificando conflitos...
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="submit" disabled={isSubmitting || isChecking}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Agendar Atendimento
          </Button>
        </div>
      </form>
    </Form>
  )
}
